import { type VariantProps, cva } from 'class-variance-authority'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { TabBadge } from './tab-badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs'
import { TabItem, TabsProps, getTabItemIndex } from './types'

const triggerVariant = cva('h-9 border-transparent', {
  variants: {
    variant: {
      default:
        'border-b-2 data-[state=active]:border-main-border-accent data-[state=active]:text-main-foreground-accent',
      danger:
        'border-b-2 data-[state=active]:border-main-border-error data-[state=active]:text-main-foreground-danger',
      warning:
        'border-b-2 data-[state=active]:border-main-border-warning data-[state=active]:text-main-foreground-warning',
      secondary: [
        'border-b-2 data-[state=active]:border-main-border-accent data-[state=active]:text-main-foreground-accent',
      ].join(' '),
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const tabsListVariant = cva('flex h-fit space-x-2 px-9 pt-2 rounded-none', {
  variants: {
    variant: {
      default: 'border-none',
      secondary: '',
      danger: 'border-none',
      warning: 'border-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type ScrollButtonProps = {
  direction: 'left' | 'right'
  onClick: () => void
}

const ScrollButton = ({ direction, onClick }: ScrollButtonProps) => {
  const isLeft = direction === 'left'
  const Icon = isLeft ? ChevronLeft : ChevronRight

  return (
    <Button
      className={cn('absolute z-10 rounded bg-palette-gray-50 px-2 hover:bg-palette-gray-200', {
        'left-0': isLeft,
        'right-0': !isLeft,
      })}
      onClick={onClick}
      variant={'ghost'}
    >
      <Icon className='size-5 text-palette-gray-500' />
    </Button>
  )
}

export type HorizontalTabsItem = 'separator' | 'arrow' | ReactNode | TabItem

export type BaseHorizontalTabsProps = Omit<TabsProps<HorizontalTabsItem>, 'onChange'> & {
  disabled?: boolean
  isLoading?: boolean
  value?: string
  defaultValue?: string
  onChange?: (tab: string) => void
  contentClassName?: string
} & VariantProps<typeof triggerVariant>

// Base component without URL params
export const BaseHorizontalTabs = ({
  items,
  className,
  disabled,
  isLoading = false,
  value: controlledValue,
  defaultValue,
  actionItem,
  contentClassName,
  variant = 'default',
  onChange,
  labelAsTitle,
}: BaseHorizontalTabsProps) => {
  const [internalValue, setInternalValue] = useState<string | undefined>(undefined)
  const tabsRef = useRef<HTMLDivElement>(null)
  const [showLeftScroll, setShowLeftScroll] = useState(false)
  const [showRightScroll, setShowRightScroll] = useState(false)

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue

  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue)
    }
    onChange?.(newValue)
  }

  const isTabItem = (item: HorizontalTabsItem): item is TabItem =>
    typeof item === 'object' && item !== null && 'label' in item && !('type' in item)

  const defaultItem = items.filter(isTabItem).find(({ itemDisabled }) => !itemDisabled)
  const defaultIndex = defaultItem && getTabItemIndex(defaultItem)
  const initialValue = defaultValue || defaultIndex

  const currentTab = useMemo(() => {
    return items.find((item) => isTabItem(item) && getTabItemIndex(item) === value)
  }, [items, value])

  const checkForScrollButtons = () => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current
      setShowLeftScroll(scrollLeft > 0)
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  useEffect(() => {
    checkForScrollButtons()
    window.addEventListener('resize', checkForScrollButtons)
    return () => window.removeEventListener('resize', checkForScrollButtons)
  }, [items])

  useEffect(() => {
    if (isTabItem(currentTab) && currentTab.itemDisabled) {
      if (!isControlled && defaultIndex) {
        // oxlint-disable-next-line react/set-state-in-effect
        setInternalValue(defaultIndex)
      }
    } else if (!isControlled && !internalValue && initialValue) {
      // Respect an explicit defaultValue (via initialValue) instead of always
      // falling back to the first tab, so a caller-provided default doesn't get
      // overridden by the first tab on mount.
      setInternalValue(initialValue)
    }
  }, [defaultIndex, initialValue, items, currentTab, isControlled, internalValue])

  const scrollTabs = (direction: 'left' | 'right') => () => {
    if (tabsRef.current) {
      const scrollAmount = Math.max(tabsRef.current.clientWidth * 0.75, 100)
      tabsRef.current.scrollBy({
        left: direction == 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  const handleScroll = () => {
    checkForScrollButtons()
  }

  return (
    <Tabs
      defaultValue={initialValue}
      value={value}
      onValueChange={handleValueChange}
      className='flex h-full min-h-0 flex-1 flex-col'
    >
      <TabsList className={cn(tabsListVariant({ variant }), className)}>
        <div className='relative flex w-full text-nowrap'>
          {showLeftScroll && <ScrollButton direction='left' onClick={scrollTabs('left')} />}

          <div
            ref={tabsRef}
            className='no-scrollbar flex justify-start gap-4 overflow-x-auto'
            onScroll={handleScroll}
          >
            {items.map((tabItem, i) => {
              if (tabItem === 'separator') {
                return <div key={i} className='h-8 w-px bg-palette-gray-200' />
              }

              if (tabItem === 'arrow') {
                return (
                  <ChevronRight
                    key={i}
                    strokeWidth={1.5}
                    className='my-auto text-palette-gray-300'
                  />
                )
              }

              if (!isTabItem(tabItem)) {
                return tabItem
              }

              const index = getTabItemIndex(tabItem)
              const { label, itemDisabled, titleIcon, disabledTooltip, badge, isLoading } = tabItem
              return (
                <Tooltip
                  key={index}
                  tooltipDisabled={!disabledTooltip || !itemDisabled}
                  trigger={
                    <TabsTrigger
                      key={index}
                      disabled={disabled || itemDisabled}
                      className={cn(triggerVariant({ variant }), 'group')}
                      value={index}
                      data-testid={`horizontal-tab-${index}`}
                    >
                      {titleIcon && <span className='size-4'>{titleIcon}</span>}
                      <span>{label}</span>
                      <TabBadge
                        variant={variant === 'warning' ? 'warning' : 'default'}
                        badge={badge}
                        isLoading={isLoading}
                      />
                    </TabsTrigger>
                  }
                >
                  {disabledTooltip}
                </Tooltip>
              )
            })}
          </div>

          {showRightScroll && <ScrollButton direction='right' onClick={scrollTabs('right')} />}
        </div>
        {actionItem}
      </TabsList>

      <Separator
        className={cn('z-10 -mt-0.25 w-full', {
          'bg-transparent': variant === 'secondary',
          'mb-6': variant !== 'secondary',
        })}
      />

      {isLoading ? (
        <Loading />
      ) : (
        items.map((tabItem) => {
          if (!isTabItem(tabItem)) {
            return null
          }

          const index = getTabItemIndex(tabItem)
          return (
            <TabsContent
              className={cn('flex-1 px-12', contentClassName)}
              key={index}
              value={index}
              forceMount={tabItem.forceMount ? true : undefined}
              data-testid={`horizontal-content-${index}`}
            >
              <div className='flex h-full min-h-0 flex-col'>
                {labelAsTitle && !tabItem.tabTitle && (
                  <TextWithIcon className='mb-6 mt-6 text-heading-medium' text={tabItem.label} />
                )}
                {tabItem.tabTitle && tabItem.tabTitle}
                {tabItem.component}
              </div>
            </TabsContent>
          )
        })
      )}
    </Tabs>
  )
}
